import { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useAutoHideControls } from "../../hooks/useAutoHideControls";
import { mediaUrl } from "../../../mediaAuth";
import { getAutolockBridge } from "../../../autolockBridge";
import { computeReelPlan, type ReelPlan } from "./reelTiming";
import type { MediaItem } from "../../../types";
import type { Track } from "../../hooks/useAudioPlayer";

const METADATA_TIMEOUT_MS = 8000;
const FALLBACK_DWELL_MS = 4000;
const FALLBACK_CROSSFADE_MS = 700;

// HTMLMediaElement.play() always returns a Promise per spec in real
// browsers, but test environments (jsdom has no real media pipeline) can
// return undefined - same guard as useAudioPlayer.ts's safePlay().
function safePlay(audio: HTMLAudioElement): void {
  audio.play()?.catch(() => {});
}

// If the track's real duration never arrives (slow network, unusual file),
// don't block the reel indefinitely - flat per-photo timing, no
// subsampling/looping, deliberately simpler than the duration-driven plan.
function fallbackPlan(photoCount: number): ReelPlan {
  const photoIndices = Array.from({ length: photoCount }, (_, i) => i);
  return { photoIndices, dwellMsPerSlot: photoIndices.map(() => FALLBACK_DWELL_MS), crossfadeMs: FALLBACK_CROSSFADE_MS };
}

// Two absolutely-stacked <img> layers, double-buffered: the layer about to
// become visible is always preloaded a full dwell period in advance (its
// src is set while it's still hidden), and the layer that just faded out
// only gets its NEXT src swapped in after its fade-out transition
// finishes - never mid-transition, which would otherwise flash the wrong
// photo for an instant.
export function ReelPlayer({
  photos,
  track,
  onExit,
  onClose,
}: {
  photos: MediaItem[];
  track: Track;
  onExit: () => void;
  onClose: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [plan, setPlan] = useState<ReelPlan | null>(null);
  const [slotIndex, setSlotIndex] = useState(0);
  const [frontIndex, setFrontIndex] = useState<0 | 1>(0);
  const [layerPhotos, setLayerPhotos] = useState<[MediaItem | null, MediaItem | null]>([null, null]);
  const [ended, setEnded] = useState(false);
  const [paused, setPaused] = useState(false);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const swapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const metadataTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { hidden: controlsHidden, onActivity, toggle: toggleControls } = useAutoHideControls(true);

  useEffect(() => {
    (document.getElementById("audioPlayer") as HTMLAudioElement | null)?.pause();
    getAutolockBridge()?.pauseInactivityForMedia();
    return () => {
      getAutolockBridge()?.resumeInactivityAfterMedia();
    };
  }, []);

  // Determine the plan: wait for the real track duration via
  // loadedmetadata, falling back to a flat per-photo timer if metadata is
  // slow or never fires.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    function onLoadedMetadata() {
      if (metadataTimeoutRef.current) {
        clearTimeout(metadataTimeoutRef.current);
        metadataTimeoutRef.current = null;
      }
      const duration = audio!.duration;
      setPlan(Number.isFinite(duration) && duration > 0 ? computeReelPlan(photos.length, duration) : fallbackPlan(photos.length));
    }

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    metadataTimeoutRef.current = setTimeout(() => {
      setPlan((prev) => prev || fallbackPlan(photos.length));
    }, METADATA_TIMEOUT_MS);

    audio.src = mediaUrl(`/assets/music/${track.filename}`);
    audio.load();

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      if (metadataTimeoutRef.current) clearTimeout(metadataTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.filename]);

  // Seed both layers and start audio once a plan exists.
  useEffect(() => {
    if (!plan) return;
    const first = photos[plan.photoIndices[0]!] ?? null;
    const second = plan.photoIndices.length > 1 ? (photos[plan.photoIndices[1]!] ?? null) : first;
    setLayerPhotos([first, second]);
    setFrontIndex(0);
    setSlotIndex(0);
    if (audioRef.current) safePlay(audioRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  function stopTimers(): void {
    if (advanceTimer.current) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
    if (swapTimer.current) {
      clearTimeout(swapTimer.current);
      swapTimer.current = null;
    }
  }

  // Schedules the next crossfade at the end of the current slot's dwell
  // time; the target layer (currently the back one) is already preloaded.
  useEffect(() => {
    if (!plan || paused || ended) return;
    const dwellMs = plan.dwellMsPerSlot[slotIndex];
    if (dwellMs === undefined) return;

    advanceTimer.current = setTimeout(() => {
      const newSlotIndex = slotIndex + 1;
      if (newSlotIndex >= plan.photoIndices.length) {
        setEnded(true);
        return;
      }
      const oldFront = frontIndex;
      const newFront: 0 | 1 = oldFront === 0 ? 1 : 0;
      setFrontIndex(newFront);
      setSlotIndex(newSlotIndex);

      // Only touch the layer that just faded out, and only once its
      // transition has actually finished.
      swapTimer.current = setTimeout(() => {
        const upcomingIndex = plan.photoIndices[newSlotIndex + 1];
        const upcomingPhoto = upcomingIndex !== undefined ? (photos[upcomingIndex] ?? null) : null;
        setLayerPhotos((prev) => {
          const next: [MediaItem | null, MediaItem | null] = [...prev];
          next[oldFront] = upcomingPhoto ?? prev[newFront];
          return next;
        });
      }, plan.crossfadeMs);
    }, dwellMs);

    return stopTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, slotIndex, frontIndex, paused, ended]);

  useEffect(() => {
    if (!ended) return;
    stopTimers();
    audioRef.current?.pause();
  }, [ended]);

  function togglePause(): void {
    setPaused((p) => {
      const next = !p;
      if (audioRef.current) {
        if (next) audioRef.current.pause();
        else safePlay(audioRef.current);
      }
      return next;
    });
  }

  function handleReplay(): void {
    if (!plan) return;
    stopTimers();
    const first = photos[plan.photoIndices[0]!] ?? null;
    const second = plan.photoIndices.length > 1 ? (photos[plan.photoIndices[1]!] ?? null) : first;
    setLayerPhotos([first, second]);
    setFrontIndex(0);
    setSlotIndex(0);
    setEnded(false);
    setPaused(false);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      safePlay(audioRef.current);
    }
  }

  const preparing = !plan || !layerPhotos[0];

  return (
    <div style={{ position: "absolute", inset: 0 }} onClick={toggleControls} onMouseMove={onActivity} onTouchStart={onActivity}>
      <audio ref={audioRef} id="reelAudioPlayer" hidden preload="auto" onEnded={() => setEnded(true)} />

      {preparing ? (
        <div className="d-flex align-items-center justify-content-center" style={{ position: "absolute", inset: 0 }}>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)" }}>
            Preparing reel…
          </Typography>
        </div>
      ) : (
        ([0, 1] as const).map((layer) => {
          const photo = layerPhotos[layer];
          if (!photo) return null;
          return (
            <img
              key={layer}
              src={mediaUrl(`/assets/thumbs/${photo.filename}`)}
              alt=""
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                opacity: frontIndex === layer ? 1 : 0,
                transition: `opacity ${plan!.crossfadeMs}ms ease-in-out`,
              }}
            />
          );
        })
      )}

      {ended && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-3"
          style={{ background: "rgba(0,0,0,0.6)" }}
        >
          <Button variant="contained" color="primary" onClick={handleReplay}>
            <i className="bi bi-arrow-clockwise me-2" aria-hidden="true" />
            Replay
          </Button>
          <Button variant="outlined" onClick={onClose} sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)" }}>
            Close
          </Button>
        </div>
      )}

      {!ended && (
        <div
          className={`position-absolute top-0 start-0 w-100 d-flex align-items-center justify-content-between p-3${controlsHidden ? " d-none" : ""}`}
        >
          <IconButton aria-label="Back" onClick={onExit} sx={{ color: "#fff", background: "rgba(255,255,255,0.15)" }}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
          </IconButton>
          <IconButton
            aria-label={paused ? "Play" : "Pause"}
            onClick={togglePause}
            sx={{ color: "#fff", background: "rgba(255,255,255,0.15)" }}
          >
            <i className={`bi ${paused ? "bi-play-fill" : "bi-pause-fill"}`} aria-hidden="true" />
          </IconButton>
          <IconButton aria-label="Close" onClick={onClose} sx={{ color: "#fff", background: "rgba(255,255,255,0.15)" }}>
            <i className="bi bi-x-lg" aria-hidden="true" />
          </IconButton>
        </div>
      )}
    </div>
  );
}
